import React, { useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useParams } from 'react-router-dom';

import { Stack } from '@mui/material';
import { BreakdownsResult, EMetric } from 'api/useAnalytics';
import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag, SortingDirection } from 'constants/enums';

import { AnalyticsBarsBox } from 'components/AnalyticsBox/AnalyticsBarsBox/AnalyticsBarsBox';
import { AnalyticsPieDonut } from 'components/AnalyticsBox/AnalyticsPieBox/AnalyticsPieDonut/AnalyticsPieDonut';
import { AnalyticsTrendsBox } from 'components/AnalyticsBox/AnalyticsTrendsBox/AnalyticsTrendsBox';
import { BreakdownSortField } from 'components/AnalyticsBox/utils';
import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode } from 'components/DatePicker/DatePicker.types';

import { useAnalyticsData } from './hooks/use-analytics-data';
import { useXAxisDateFormat } from './hooks/useXAxisDateFormat';
import { useAnalyticsChartData } from './useAnalyticsChartData.hook';
import {
  DEFAULT_DATE_RANGE,
  FINANCE_ANALYTICS,
  FINANCE_BREAKDOWNS_ANALYTICS,
  FINANCE_BREAKDOWNS_ANALYTICS_SCIPLAY,
  getFixedDays,
  getWholeRange,
} from './utils';

const getFinanceBreakdowns = (isSciPlay: boolean): EMetric[] => {
  if (isSciPlay) {
    return FINANCE_BREAKDOWNS_ANALYTICS_SCIPLAY;
  }
  return FINANCE_BREAKDOWNS_ANALYTICS;
};

const AnalyticsFinanceTab: React.FC = () => {
  const { publisherId } = useParams();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [previousDateRange, setPreviousDateRange] = useState<
    DateRange | undefined
  >();

  const { featureFlags } = useFeatureFlags({ publisherId });

  const shouldDisplaySciPlayAnalytics =
    featureFlags?.[
      EFeatureFlag.CHECKOUT_SCIPLAY_ANALYTICS
    ];

  const financeBreakdowns = getFinanceBreakdowns(!!shouldDisplaySciPlayAnalytics);

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);
  const apiPreviousDateRange = useMemo(
    () => getWholeRange(previousDateRange),
    [previousDateRange]
  );

  const { analytics, previousAnalytics, isLoading } = useAnalyticsData({
    apiDateRange,
    apiPreviousDateRange,
    publisherId,
    statsMetrics: FINANCE_ANALYTICS,
    trendsMetrics: FINANCE_ANALYTICS,
    breakdownsMetrics: financeBreakdowns,
  });

  const handleDateRangeChange = (value: DateRange) => {
    setDateRange(value);
    setPreviousDateRange(undefined);
  };

  const formattedAnalytics = useAnalyticsChartData({
    stats: analytics.getAnalytics.data,
    trends: analytics.getTrends.data,
    previousStats: previousAnalytics.getAnalytics.data,
    previousTrends: previousAnalytics.getTrends.data,
    previousDateRange: previousDateRange,
    currentDateRange: dateRange
  });

  const xAxisDateFormat = useXAxisDateFormat(analytics);

  const breakdownAnalytics = financeBreakdowns.map((metric) => {
    return analytics.getBreakdowns.data?.find(
      (breakdown) => breakdown.name === metric
    );
  });

  return (
    <Stack direction="column" className="pb-[57px]">
      <div className="w-full mb-4 flex justify-end items-center pb-2">
        <div className="flex items-center w-[50%] gap-2">
          <DatePicker
            shouldDisplayTime={false}
            mode={DatePickerMode.RANGE}
            value={dateRange}
            onApply={(value) => handleDateRangeChange(value as DateRange)}
            shouldExcludeFuture
          />
          <DatePicker
            shouldDisplayTime={false}
            mode={DatePickerMode.RANGE}
            value={previousDateRange}
            onApply={(value) => setPreviousDateRange(value as DateRange)}
            onClear={() => setPreviousDateRange(undefined)}
            placeholder="Compare Dates"
            disabled={
              dateRange?.from === undefined || dateRange?.to === undefined
            }
            shouldExcludeFuture
            disableDatesAfter={dateRange.to}
            fixedRangeDays={getFixedDays(dateRange)}
          />
        </div>
      </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {breakdownAnalytics.map((breakdown, i) => {
          if(!breakdown) return null;

          const isRevenuePerCountry = breakdown.name === EMetric.REVENUE_PER_COUNTRY;
          const isOrderSuccessRateByPaymentMethod = breakdown.name === EMetric.ORDER_SUCCESS_RATE_BY_PAYMENT_METHOD;

          if (isRevenuePerCountry || isOrderSuccessRateByPaymentMethod) {
            const currentBreakdown = breakdown;
            const previousBreakdown = previousAnalytics.getBreakdowns.data?.find(
              (prev: BreakdownsResult) => prev.name === breakdown.name
            );
            
            return (
              <AnalyticsBarsBox
                key={breakdown.name + i}
                isLoading={isLoading}
                metricName={breakdown.name}
                currentBreakdown={currentBreakdown}
                comparedBreakdown={previousBreakdown}
                dateRange={dateRange}
                comparedDateRange={previousDateRange}
                sortConfig={
                  isRevenuePerCountry || isOrderSuccessRateByPaymentMethod
                    ? {
                        field: BreakdownSortField.VALUE,
                        direction: SortingDirection.DESC
                      }
                    : undefined
                }
              />
            );
          }

          return (
            <AnalyticsPieDonut
              key={breakdown.name + i}
              breakdown={breakdown}
              isLoading={isLoading}
            />
          );
        })}
        {FINANCE_ANALYTICS.map((metricName, i) => {
          const item = formattedAnalytics[metricName];
          return (
            <AnalyticsTrendsBox
              key={metricName + i}
              metricName={metricName}
              stat={item?.stat}
              currentTrends={item?.currentTrends ?? []}
              previousTrends={item?.previousTrends}
              isLoading={isLoading}
              xAxisDateFormat={xAxisDateFormat}
            />
          );
        })}
      </div>
    </Stack>
  );
};

export default AnalyticsFinanceTab;
